import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { Tabs, Tab } from "react-bootstrap";

const getRatioColor = (ratio) => {
  if (ratio <= 5) return "text-green-700";
  if (ratio <= 10) return "text-green-500";
  if (ratio <= 15) return "text-yellow-500";
  if (ratio <= 20) return "text-orange-500";
  if (ratio <= 30) return "text-red-500";
  return "text-red-700";
};

const MultiDayPlanner = ({ data, onChange }) => {
  const [activeKey, setActiveKey] = useState(null);
  const [showCopiedAlert, setShowCopiedAlert] = useState(false);

  const preferredDate = moment(data?.preferredDate, "YYYY-MM-DD");

  const defaultDateEntry = {
    date: preferredDate.format("YYYY-MM-DD"),
    startTime: data?.startTime || "",
    endTime: data?.endTime || "",
    activity: "",
    activityNotes: "",
    locations: [],
    staff: [],
    students: [],
  };

  const allDates =
    Array.isArray(data?.multiDays) && data.multiDays.length > 0
      ? [...data.multiDays]
      : [defaultDateEntry];

  useEffect(() => {
    if (onChange) {
      onChange("multiDays", allDates);
      setActiveKey(allDates[0].date);
    }
    // eslint-disable-next-line
  }, []);

  if (!data || !data.preferredDate || typeof onChange !== "function") {
    return (
      <div className="text-sm text-gray-500 italic">Loading planner...</div>
    );
  }

  const timeOptions = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 5) {
      const time = moment().hour(hour).minute(minute);
      timeOptions.push({
        value: time.format("HH:mm"),
        label: time.format("h:mm a"),
      });
    }
  }

  const existingStaff = data.staff?.attending || [];

  const existingLocations = data.locations || [];

  const formatStudentLabel = (s) =>
    s.stSurname && s.stPrfName
      ? `${s.stSurname}, ${s.stPrfName} (Yr ${s.stYrLvl ?? "?"}, ${
          s.stHmrm || ""
        })`
      : s.stud_name || s.label || "Unnamed student";

  const customStudentIds = new Set(
    (data.students?.customStudents || []).map((s) => s.value)
  );

  const individualStudents = Object.values(data.students?.studentMap || {})
    .filter((s) => !customStudentIds.has(s.stud_code))
    .map((s) => ({
      value: s.stud_code,
      label: formatStudentLabel(s),
    }));

  const formattedCustomStudents = (data.students?.customStudents || []).map(
    (s) => ({
      value: s.value,
      label: s.label,
    })
  );

  const sortedIndividuals = [
    ...individualStudents,
    ...formattedCustomStudents,
  ].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
  );

  const sortedGroups = (data.students?.selectedGroups || []).map((g) => ({
    label: g.label,
    value: g.value,
  }));

  const studentOptions = [...sortedGroups, ...sortedIndividuals];

  const handleDateSelect = (date) => {
    const formatted = moment(date).format("YYYY-MM-DD");
    if (moment(formatted).isBefore(preferredDate)) return;
    const alreadyExists = allDates.find((d) => d.date === formatted);
    if (!alreadyExists) {
      const updated = [...allDates, { ...defaultDateEntry, date: formatted }];
      onChange("multiDays", updated);
      setActiveKey(formatted);
    }
  };

  const updateDay = (dateKey, field, value) => {
    const updated = allDates.map((day) =>
      day.date === dateKey ? { ...day, [field]: value } : day
    );
    onChange("multiDays", updated);
  };

  const removeDay = (dateKey) => {
    if (dateKey === preferredDate.format("YYYY-MM-DD")) return;
    const updated = allDates.filter((d) => d.date !== dateKey);
    onChange("multiDays", updated);
    if (activeKey === dateKey && updated.length > 0) {
      setActiveKey(updated[0].date);
    }
  };

  const resetDay = (dateKey) => {
    const updated = allDates.map((d) =>
      d.date === dateKey ? { ...defaultDateEntry, date: d.date } : d
    );
    onChange("multiDays", updated);
  };

  const copyDetailsToAll = (sourceDate) => {
    const source = allDates.find((d) => d.date === sourceDate);
    const updated = allDates.map((d) =>
      d.date === sourceDate ? d : { ...d, ...source, date: d.date }
    );
    onChange("multiDays", updated);
    setShowCopiedAlert(true);
    setTimeout(() => setShowCopiedAlert(false), 2500);
  };

  const sortedDates = [...allDates].sort((a, b) =>
    moment(a.date).diff(moment(b.date))
  );

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-800">Multi-Day Planner</h2>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Add Date
        </label>
        <DatePicker
          onChange={handleDateSelect}
          minDate={preferredDate.toDate()}
          excludeDates={sortedDates.map((d) => moment(d.date).toDate())}
          placeholderText="Click to select a date"
          className="border px-3 py-2 rounded-md"
        />
      </div>

      <Tabs
        activeKey={activeKey}
        onSelect={(k) => setActiveKey(k)}
        id="multi-day-tabs"
        className="mb-3"
      >
        {sortedDates.map((day) => {
          const fullStudentMap = data.students?.studentMap || {};
          const fullGroupMap = data.students?.groupStudentsMap || {};
          const selectedDayStudents = day.students || [];

          const staffUnknown = data.staff?.staffUnknown;
          const studentsUnknown =
            data.students?.notYetConfirmed ||
            data.students?.noStudentsAttending;

          const studentIds = new Set();
          if (!studentsUnknown) {
            selectedDayStudents.forEach((entry) => {
              const val = entry?.value;
              if (!val) return;

              if (
                val.startsWith("grp_") ||
                val.startsWith("cohort_") ||
                val.startsWith("house_")
              ) {
                const groupSet = fullGroupMap[val];
                if (Array.isArray(groupSet) || groupSet instanceof Set) {
                  [...groupSet].forEach((id) => {
                    if (fullStudentMap[id]) studentIds.add(id);
                  });
                }
              } else {
                if (fullStudentMap[val]) studentIds.add(val);
              }
            });
          }

          const approxStudents = studentIds.size;

          const staffCount = staffUnknown
            ? parseInt(data.staff?.staffUnknownDetails?.approxNumStaff || 0)
            : day.staff.length;

          const validRatio =
            !staffUnknown &&
            !studentsUnknown &&
            approxStudents > 0 &&
            staffCount > 0;
          const ratioVal = validRatio
            ? Math.ceil(approxStudents / staffCount)
            : null;
          const ratio = ratioVal ? `1:${ratioVal}` : null;
          const ratioColor = ratioVal ? getRatioColor(ratioVal) : "";

          return (
            <Tab
              eventKey={day.date}
              title={moment(day.date).format("ddd, DD MMM")}
              key={day.date}
            >
              <div className="border rounded-lg p-4 mb-6 shadow-sm space-y-3 bg-gray-50">
                <div className="flex justify-between items-start mb-2">
                  <div className="text-sm text-gray-600">
                    {moment(day.date).format("dddd, D MMMM YYYY")}
                    {ratio && (
                      <span
                        className={`ml-4 text-xs font-medium ${ratioColor}`}
                      >
                        Staff : Student Ratio = {ratio}
                      </span>
                    )}
                  </div>
                  <div className="space-x-4">
                    <button
                      onClick={() => resetDay(day.date)}
                      className="text-sm text-yellow-600 hover:underline"
                    >
                      Reset
                    </button>
                    {day.date !== preferredDate.format("YYYY-MM-DD") && (
                      <button
                        onClick={() => removeDay(day.date)}
                        className="text-sm text-red-600 hover:underline"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Time
                    </label>
                    <Select
                      options={timeOptions}
                      value={
                        timeOptions.find(
                          (opt) => opt.value === day.startTime
                        ) || null
                      }
                      onChange={(opt) =>
                        updateDay(day.date, "startTime", opt?.value || "")
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Time
                    </label>
                    <Select
                      options={timeOptions}
                      value={
                        timeOptions.find((opt) => opt.value === day.endTime) ||
                        null
                      }
                      onChange={(opt) => {
                        const val = opt?.value || "";
                        const start = moment(day.startTime, "HH:mm");
                        if (
                          start.isValid() &&
                          moment(val, "HH:mm").isSameOrBefore(start)
                        ) {
                          alert("End time must be after start time");
                          return;
                        }
                        updateDay(day.date, "endTime", val);
                      }}
                    />
                  </div>

                  <div className="col-span-1 md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Activity Description
                    </label>
                    <input
                      type="text"
                      className="w-full border px-3 py-2 rounded-md"
                      value={day.activity || ""}
                      onChange={(e) =>
                        updateDay(day.date, "activity", e.target.value)
                      }
                    />
                  </div>

                  <div className="col-span-1 md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Activity Notes (optional)
                    </label>
                    <textarea
                      className="w-full border px-3 py-2 rounded-md"
                      rows={3}
                      value={day.activityNotes || ""}
                      onChange={(e) =>
                        updateDay(day.date, "activityNotes", e.target.value)
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Locations
                    </label>
                    <Select
                      isMulti
                      options={existingLocations.map((l) => ({
                        label: l.address,
                        value: l.address,
                      }))}
                      value={day.locations.map((loc) => ({
                        label: loc,
                        value: loc,
                      }))}
                      onChange={(opts) =>
                        updateDay(
                          day.date,
                          "locations",
                          opts.map((o) => o.value)
                        )
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Staff
                    </label>
                    {staffUnknown ? (
                      <div className="text-sm italic text-gray-500">
                        Staff to be confirmed
                      </div>
                    ) : (
                      <Select
                        isMulti
                        options={existingStaff.map((s) => ({
                          label: s.label,
                          value: s.value,
                        }))}
                        value={day.staff}
                        onChange={(opts) =>
                          updateDay(day.date, "staff", opts || [])
                        }
                      />
                    )}
                  </div>

                  <div className="col-span-1 md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Students
                    </label>
                    {studentsUnknown ? (
                      <div className="text-sm italic text-gray-500">
                        {data.students?.noStudentsAttending
                          ? "No students attending"
                          : "Student attendance to be confirmed"}
                      </div>
                    ) : (
                      <Select
                        isMulti
                        options={studentOptions}
                        value={day.students}
                        onChange={(opts) =>
                          updateDay(day.date, "students", opts || [])
                        }
                      />
                    )}
                  </div>
                </div>

                {sortedDates.length > 1 && (
                  <div className="text-right pt-3 relative">
                    <button
                      onClick={() => copyDetailsToAll(day.date)}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Copy these details to all other days
                    </button>

                    {showCopiedAlert && (
                      <div className="absolute right-0 -top-10 bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded shadow animate-fadeOut transition-opacity duration-1000">
                        ✅ Details copied!
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Tab>
          );
        })}
      </Tabs>
    </div>
  );
};

export default MultiDayPlanner;
